import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../../components/buttons/button";
import { ContainerCard } from "../../../../../components/cards/containerCard";
import { FormCard } from "../../../../../components/cards/formCard";
import {
  InformationCard,
  InformationCardNoIcon,
} from "../../../../../components/cards/infoCard";
import { TitleSub } from "../../../../../components/typography/titleSub";
import {
  formatToCurrency,
  splitCamelCase,
} from "../../../../../helperFunctions";
import {
  useGetDefaultMembershipFormQuery,
  useGetMembershipFormQuery,
} from "../../../../../redux/features/cooperative/cooperative.api";
import { SingleCooperativeMembershipFormField } from "../../../../../redux/features/cooperative/cooperative.type";
import { useAppSelector } from "../../../../../redux/hooks";
import {
  MEMBERSHIP_FORM_FIELD_ICONS_MAP,
  MEMBERSHIP_FORM_FIELD_TITLE_MAP,
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../../utils/constants";

export const MembershipSettingsView = () => {
  const [allFormFields, setAllFormFields] = useState<
    SingleCooperativeMembershipFormField[]
  >([]);

  const navigate = useNavigate();

  const { onboardingCompletionStatus } = useAppSelector((state) => {
    return state?.auth;
  });

  const { data: defaultFormFields, isFetching: fetchingdefaultFormFields } =
    useGetDefaultMembershipFormQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const { data: membershipForm, isFetching: fetchingMembershipform } =
    useGetMembershipFormQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  useEffect(() => {
    setAllFormFields([
      ...(membershipForm?.data?.Field ?? []).map((v) => ({
        question: v.question,
        fieldType: v.fieldType,
        option: [],
        isDefault: false,
      })),
      ...(defaultFormFields?.data ?? []).map((v) => ({
        question: v.question,
        fieldType: v.fieldType,
        option: [],
        isDefault: true,
      })),
    ]);
  }, [membershipForm, defaultFormFields, onboardingCompletionStatus]);

  return (
    <div>
      <div>
        {/* Information on how to edit (only shows when account hasn't made any edit) */}

        <InformationCardNoIcon
          className="width-lim:max-w-[653px] mb-3"
          info={
            <p>
              Click the{" "}
              <span className="text-pry-green-200 font-medium">Edit</span>{" "}
              button to set registration fee and membership form
            </p>
          }
        />

        {/* Main Page Content  */}
        <div className="w-full flex flex-row flex-wrap width-lim:flex-nowrap gap-2 md:gap-6">
          <ContainerCard
            className="
            w-full width-lim:max-w-[653px] width-lim:min-h-[635px] !px-0
            flex flex-col order-last width-lim:order-first
            "
          >
            <div className="px-4 md:px-8 pb-6 border-b border-pry-dark-green-400/5">
              {fetchingMembershipform || fetchingdefaultFormFields ? (
                <div className="flex flex-col gap-2">
                  <Skeleton
                    width={"100%"}
                    height={40}
                    baseColor={SKELETON_BASE_COLOR}
                    highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  />

                  <Skeleton
                    width={"20%"}
                    height={40}
                    baseColor={SKELETON_BASE_COLOR}
                    highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  />
                </div>
              ) : (
                <>
                  <div className="w-full flex flex-row flex-wra gap-4 items-center justify-between">
                    <TitleSub
                      title="Set Registration Fee"
                      subtitle="This is the amount prospective members will be charged"
                      titleClassName=""
                      subtitleClassName=""
                      className=""
                    />

                    <Button
                      text="Edit"
                      onClick={() => {
                        navigate("/app/settings/membership-settings/edit");
                      }}
                      type="button"
                      className="!min-w-[82px] md:!min-w-[107px] !w-fit"
                      size="medium"
                      loading={false}
                      disabled={false}
                      variation="primary"
                    />
                  </div>

                  <p className="mt-4 text-pry-green-200 font-medium text-2xl">
                    {onboardingCompletionStatus?.isMembershipFormComplete
                      ? `${formatToCurrency(
                          membershipForm?.data?.Form?.Fee ?? 0,
                          { decimalPlaces: 2, currency: "NGN" },
                        )}`
                      : "Set a Fee"}
                  </p>
                </>
              )}
            </div>

            {/* For setup preview  */}
            <div className="px-4 md:px-8 pt-6 overflow-scroll flex flex-col">
              <TitleSub
                title="Membership Form"
                subtitle="Below are the fields(information) that will be collected from prospective members"
                titleClassName=""
                subtitleClassName=""
                className=""
              />

              <div className="mt-[16px] gap-2 grid grid-cols-2 md:grid-cols-3">
                {fetchingMembershipform || fetchingdefaultFormFields ? (
                  <>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((k) => {
                      return (
                        <Skeleton
                          width={"100%"}
                          height={50}
                          baseColor={SKELETON_BASE_COLOR}
                          highlightColor={SKELETON_HIGHLIGHT_COLOR}
                          key={`${k}`}
                          className="rounded-lg"
                        />
                      );
                    })}
                  </>
                ) : (
                  allFormFields?.map((field, idx) => {
                    const key = `membershipFormField-${idx}`;

                    return (
                      <FormCard
                        title={MEMBERSHIP_FORM_FIELD_TITLE_MAP[field.fieldType]}
                        text={splitCamelCase(field.question)}
                        icon={MEMBERSHIP_FORM_FIELD_ICONS_MAP[field.fieldType]}
                        mode="view"
                        key={key}
                      />
                    );
                  })
                )}
              </div>
            </div>
          </ContainerCard>

          <InformationCard
            className="w-full width-lim:max-w-[345px] h-fit width-lim:sticky-top"
            info={
              <p>
                <span className="text-pry-green-200">Membership form</span> is
                what a prospective member will fill before joining your
                cooperative. You can make changes to the form here anytime
              </p>
            }
          />
        </div>
      </div>
    </div>
  );
};
